import PropTypes from "prop-types";
import {
  ActivityWrapper,
  PillRow,
  QuietText,
  MetaRow,
  ActivityPill,
} from "./MatterActivityCellStyled";

/**
 * Activity cell for the matter "home" list.
 *
 * Answers "which matters need me today?" without asking the user to drill in.
 * Up to three small pills — {N new} / {N review} / {N processing} — plus a
 * last-touched relative string and total doc count.
 *
 * All fields are rollups the backend doesn't yet expose on `MatterSerializer`
 * (`documents_total`, `documents_review_count`, `documents_processing_count`,
 * `new_since_last_open`, `last_activity_at`). Until the backend ships them,
 * every matter falls through to the "quiet" zero-state — the cell renders
 * without throwing, and the row still looks correct.
 */
const MatterActivityCell = ({
  newCount,
  reviewCount,
  processingCount,
  lastTouched,
  docsTotal,
}) => {
  const pills = [];
  if (newCount > 0) {
    pills.push(
      <ActivityPill key="new" $tone="info" aria-label={`${newCount} new`}>
        {newCount} new
      </ActivityPill>
    );
  }
  if (reviewCount > 0) {
    pills.push(
      <ActivityPill
        key="review"
        $tone="warning"
        aria-label={`${reviewCount} need review`}
      >
        {reviewCount} review
      </ActivityPill>
    );
  }
  if (processingCount > 0) {
    pills.push(
      <ActivityPill
        key="proc"
        $tone="neutral"
        aria-label={`${processingCount} processing`}
      >
        {processingCount} processing
      </ActivityPill>
    );
  }

  const hasPills = pills.length > 0;
  const hasMeta = Boolean(lastTouched) || typeof docsTotal === "number";

  return (
    <ActivityWrapper>
      {hasPills ? <PillRow>{pills}</PillRow> : <QuietText>quiet</QuietText>}
      {hasMeta && (
        <MetaRow>
          {lastTouched && <span>{lastTouched}</span>}
          {lastTouched && typeof docsTotal === "number" && <span>·</span>}
          {typeof docsTotal === "number" && (
            <span>
              {docsTotal} {docsTotal === 1 ? "doc" : "docs"}
            </span>
          )}
        </MetaRow>
      )}
    </ActivityWrapper>
  );
};

MatterActivityCell.propTypes = {
  newCount: PropTypes.number,
  reviewCount: PropTypes.number,
  processingCount: PropTypes.number,
  lastTouched: PropTypes.string,
  docsTotal: PropTypes.number,
};

MatterActivityCell.defaultProps = {
  newCount: 0,
  reviewCount: 0,
  processingCount: 0,
};

export default MatterActivityCell;
